import useAutocomplete, { AutocompleteInputChangeReason } from '@material-ui/lab/useAutocomplete';
import isEmpty from 'lodash/isEmpty';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import useComponentVisible from '@/hooks/useComponentVisible';
import {
  ILocationSearchAutocompleteListOption,
  useLocationSearchAutocompleteOptions,
} from '@/hooks/useLocationSearchAutocompleteOptions';
import { useLocationSearchSuggestions } from '@/hooks/useLocationSearchSuggestions';
import { IHeaderSearchFilterDates, IHeaderSearchFilterGuests } from '@/redux/modules/globalHeader';
import { ILodgingCampgrounds } from '@/services/types/search/lodgingCampgrounds';
import { IVehicle } from '@/services/types/search/vehicle';
import { IVehicleCampgrounds } from '@/services/types/search/vehicleCampgrounds';
import { IGroupOption, limitRecentSearches } from '@/utility/autoCompleteGroupedOptions';
import { formatDateFullRange } from '@/utility/format-date';
import { stripDiacritics } from '@/utility/stripDiacritics';
import {
  LocationGroupType,
  mapLocationLabelToGroupName,
  setSurfacedLocation,
} from '@/utility/surfacedLocation';

import { EHeaderTab, IHeaderMenu } from '../UniversalSearchContainer/menu';
import DateContent from './DateContent/DateContent';
import classes from './DesktopUniversalSearch.module.css';
import DetailsContent from './DetailsContent/DetailsContent';
import LocationSearchContent from './LocationContent/LocationSearchContent';
import LocationSearchInput from './LocationSearchInput';
import LodgingCampgroundsContent from './LodgingCampgroundsContent/LodgingCampgroundsContent';
import {
  DateTab,
  GuestsTab,
  SearchButton,
  VehicleDetailCampgroundsTab,
  VehicleDetailTab,
} from './SearchTabs';
import VehicleCampgroundsContent from './VehicleCampgroundsContent/VehicleCampgroundsContent';
import VehicleContent from './VehicleContent/VehicleContent';

export enum SEARCH_TAB {
  DATES = 'DATES',
  DETAILS = 'DETAILS',
  LOCATION = 'LOCATION',
  VEHICLE = 'VEHICLE',
  VEHICLE_CAMPGROUNDS = 'VEHICLE_CAMPGROUNDS',
  LODGING_CAMPGROUNDS = 'LODGING_CAMPGROUNDS',
}

export const getClassName = (
  selectedTab: SEARCH_TAB | null,
  currentTab: SEARCH_TAB,
  isStayFilterEnabled?: boolean,
) => {
  const className = () => {
    if (selectedTab === currentTab) {
      return 'bg-white';
    }

    if (selectedTab !== null) {
      return 'border-b border-grey-200 bg-gray-100 rounded-bl-none';
    }

    return isStayFilterEnabled ? 'bg-white' : 'bg-gray-100';
  };

  if (
    [
      SEARCH_TAB.DATES,
      SEARCH_TAB.DETAILS,
      SEARCH_TAB.VEHICLE,
      SEARCH_TAB.LODGING_CAMPGROUNDS,
      SEARCH_TAB.VEHICLE_CAMPGROUNDS,
    ].includes(currentTab)
  ) {
    return `${className()} ${classes.date_tab}`;
  }

  if (currentTab === SEARCH_TAB.LOCATION && selectedTab && selectedTab !== SEARCH_TAB.LOCATION) {
    return `${className()} ${classes.active_location_tab}`;
  }

  return className();
};

interface DesktopUniversalSearchProps {
  onSubmit?: () => void;
  handleRecentSearchNavigation?: (url: string) => void;
  location?: { lat: number; lng: number };
  address?: string;
  guests?: IHeaderSearchFilterGuests;
  dates?: IHeaderSearchFilterDates;
  vehicle?: IVehicle;
  vehicleCampgrounds?: IVehicleCampgrounds;
  lodgingCampgrounds?: ILodgingCampgrounds;
  recentSearches?: ILocationSearchAutocompleteListOption[];
  maxRecentSearches: number;
  loading?: boolean;
  onChangeAddress?: (address?: string, addressOption?: any) => void;
  onChangeDates?: (address?: IHeaderSearchFilterDates) => void;
  onChangeGuests?: (guests?: IHeaderSearchFilterGuests) => void;
  onConfirmAddress?: (address?: string) => void;
  onConfirmDates?: (dates?: IHeaderSearchFilterDates) => void;
  onConfirmGuests?: (guests: IHeaderSearchFilterGuests) => void;
  onChangeVehicle?: (vehicle?: IVehicle) => void;
  onChangeVehicleCampgrounds?: (vehicle?: IVehicleCampgrounds) => void;
  onChangeLodgingCampgrounds?: (vehicle?: ILodgingCampgrounds) => void;
  openedTab?: IHeaderMenu;
  popularDestinations?: { label: string }[];
  isCategoryEnabled?: boolean;
  isStayFilterEnabled?: boolean;
  isGuestOccupancyAvailable?: boolean;
  isCampground?: boolean;
}

const DesktopUniversalSearch: React.FC<DesktopUniversalSearchProps> = ({
  onSubmit,
  location,
  address,
  dates,
  guests,
  vehicle,
  vehicleCampgrounds,
  lodgingCampgrounds,
  recentSearches = [],
  maxRecentSearches,
  loading = false,
  onChangeAddress,
  onChangeDates,
  onChangeGuests,
  onConfirmAddress,
  onConfirmDates,
  onConfirmGuests,
  onChangeVehicle,
  onChangeVehicleCampgrounds,
  onChangeLodgingCampgrounds,
  openedTab,
  popularDestinations = [],
  isCategoryEnabled,
  isStayFilterEnabled,
  handleRecentSearchNavigation,
  isGuestOccupancyAvailable,
  isCampground,
}) => {
  const { ref, isComponentVisible, setIsComponentVisible } = useComponentVisible(true);
  const hasConfirmedOnCloseRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState<SEARCH_TAB | null>(null);
  const [addressValue, setAddressValue] = useState(address || '');

  const suggestions = useLocationSearchSuggestions(
    selectedTab === SEARCH_TAB.LOCATION ? addressValue : '',
    location ? [location.lng, location.lat] : undefined,
  );

  const formattedDates = useMemo<string | null>(() => {
    if (!dates?.from || !dates?.to) {
      return null;
    }

    return formatDateFullRange(dates.from.toString(), dates.to.toString());
  }, [dates]);

  useEffect(() => {
    if (address !== addressValue) {
      setAddressValue(address || '');
    }
  }, [address, addressValue]);

  const { options, optionGroupLabels } = useLocationSearchAutocompleteOptions({
    shouldBuildGroupedOptions: false,
    suggestions,
    recentSearches,
    popularDestinations,
  });

  const handleAddressChange = (
    _e: React.ChangeEvent<Record<string, string>>,
    value: string,
    reason: AutocompleteInputChangeReason,
  ) => {
    if (reason === 'input') {
      onChangeAddress?.(value);
      setAddressValue(value);
    }
    setSurfacedLocation(LocationGroupType.OTHER);
  };

  const handleAddressSelect = (_e: React.ChangeEvent<Record<string, string>>, option: any) => {
    if (!option) return;
    const { label, url, groupName, value } = option;

    if (value?.url) {
      handleRecentSearchNavigation?.(url);
      return;
    }

    if (value?.name) {
      onChangeAddress?.(value.name, option);
      setAddressValue(value.name);
    } else if (value?.address) {
      onChangeAddress?.(value.address, option);
      setAddressValue(value.address);
    } else {
      onChangeAddress?.(label, option);
      setAddressValue(label);
    }

    const groupType = mapLocationLabelToGroupName(groupName);
    setSurfacedLocation(groupType);

    if (openedTab?.tab === EHeaderTab.TOWABLE) {
      if (!vehicle && label) {
        setSelectedTab(SEARCH_TAB.VEHICLE);
      }
    } else {
      if (!dates && label) {
        setSelectedTab(SEARCH_TAB.DATES);
      }
    }

    if (dates && label) {
      onConfirmAddress?.(label);
      setSelectedTab(null);
    }
  };

  const handleChangeDate = (value?: IHeaderSearchFilterDates) => {
    if ((value?.from && value?.to) || isEmpty(value)) {
      onChangeDates?.(value);

      // When both dates are selected, automatically move focus to guests tab
      if (value?.from && value?.to) {
        handleClickTab(SEARCH_TAB.DETAILS);
      }
    }
  };

  const handleChangeVehicle = (v?: IVehicle) => {
    if (v?.year && v?.model && v?.make) {
      setSelectedTab(SEARCH_TAB.DATES);
    }
    onChangeVehicle?.(v);
  };

  const handleChangeVehicleCampgrounds = (v?: IVehicleCampgrounds) => {
    onChangeVehicleCampgrounds?.(v);
  };

  const handleChangeLodgingCampgrounds = (lodging?: ILodgingCampgrounds) => {
    onChangeLodgingCampgrounds?.(lodging);
  };

  const handleOpen = () => {
    setSelectedTab(SEARCH_TAB.LOCATION);
  };

  const { getInputProps, getListboxProps, getOptionProps, groupedOptions, focused } =
    useAutocomplete({
      inputValue: addressValue,
      options,
      filterOptions: (autocompleteOptions, autocompleteState) => {
        const input = stripDiacritics(autocompleteState.inputValue.trim().toLowerCase());

        if (input.length <= 2) {
          return autocompleteOptions;
        }

        return autocompleteOptions.filter(autocompleteOption => {
          // Filter everything except suggestions
          if (autocompleteOption.groupName === optionGroupLabels.suggestions) {
            return autocompleteOption;
          }

          const candidate = stripDiacritics(
            autocompleteState.getOptionLabel(autocompleteOption).trim().toLowerCase(),
          );

          return candidate.indexOf(input) > -1;
        });
      },
      getOptionLabel: option => option.label,
      groupBy: option => option.groupName,
      onInputChange: handleAddressChange,
      onChange: handleAddressSelect,
      onOpen: handleOpen,
      freeSolo: true,
      open: selectedTab === SEARCH_TAB.LOCATION,
    });

  const handleClickTab = (tab: SEARCH_TAB) => {
    if (tab !== SEARCH_TAB.LOCATION && tab === selectedTab) {
      setSelectedTab(null);
    } else {
      setSelectedTab(tab);
    }
    setIsComponentVisible(true);
  };

  useEffect(() => {
    if (focused) {
      handleClickTab(SEARCH_TAB.LOCATION);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focused]);

  // Reset the close guard when the dropdown re-opens
  useEffect(() => {
    if (isComponentVisible) {
      hasConfirmedOnCloseRef.current = false;
    }
  }, [isComponentVisible]);

  // action when closing dropdown
  useEffect(() => {
    if (!isComponentVisible && !hasConfirmedOnCloseRef.current) {
      hasConfirmedOnCloseRef.current = true;
      if (selectedTab === SEARCH_TAB.DETAILS && guests) {
        onConfirmGuests?.(guests);
      }
      if (selectedTab === SEARCH_TAB.DATES && dates) {
        onConfirmDates?.(dates);
      }
      setSelectedTab(null);
    }
  }, [isComponentVisible, guests, onConfirmGuests, onConfirmDates, dates, selectedTab]);

  // we may hide some recent searches but require all options to calculate the index into the
  // results for getting option props (onClick handler)
  const allGroupOptions = groupedOptions as unknown as IGroupOption[];
  const groupOptions = limitRecentSearches(
    groupedOptions as unknown as IGroupOption[],
    optionGroupLabels.recentSearches,
    maxRecentSearches,
  );

  const handleSubmit = (e: React.BaseSyntheticEvent) => {
    e.preventDefault();
    onSubmit?.();
    setSelectedTab(null);
  };

  const dateTabText = formattedDates
    ? `${formattedDates} ${dates?.flexible_days ? ` (±${dates?.flexible_days})` : ''}`
    : '';

  const isTowable = openedTab?.tab === EHeaderTab.TOWABLE;
  const isRVSite = openedTab?.tab === EHeaderTab.RV_SITE;
  const isLodgingSite = openedTab?.tab === EHeaderTab.LODGING_SITE;
  const needsExtraRow = isTowable || isRVSite;
  const isDelivery = openedTab?.tab === EHeaderTab.DELIVERABLES;

  return (
    <>
      <form
        className={`
        ${classes.search_homepage}
        ${selectedTab ? 'bg-white shadow-xl' : 'bg-gray-100'}
        ${isStayFilterEnabled ? classes.hasStayFilter : ''}
      `}
        onSubmit={handleSubmit}
        ref={ref}>
        <div className={`${classes.search_wrapper} ${needsExtraRow ? classes.isTowable : ''}`}>
          <LocationSearchInput
            isTowables={needsExtraRow}
            selectedTab={selectedTab}
            className={`${getClassName(selectedTab, SEARCH_TAB.LOCATION, isStayFilterEnabled)} ${
              classes.search_location
            } ${needsExtraRow ? classes.isTowable : ''}`}
            getInputProps={getInputProps}
            isCategoryEnabled={isCategoryEnabled}
            isStayFilterEnabled={isStayFilterEnabled}
          />
          {isTowable && (
            <VehicleDetailTab
              onClick={() => handleClickTab(SEARCH_TAB.VEHICLE)}
              selectedTab={selectedTab}
              vehicle={vehicle}
              isStayFilterEnabled={isStayFilterEnabled}
            />
          )}
          {isRVSite && (
            <VehicleDetailCampgroundsTab
              onClick={() => handleClickTab(SEARCH_TAB.VEHICLE_CAMPGROUNDS)}
              selectedTab={selectedTab}
              vehicle={vehicleCampgrounds}
              isStayFilterEnabled={isStayFilterEnabled}
            />
          )}
          {/* TODO: readd lodging campground if we end up supporting lodging type */}
          <DateTab
            isTowables={needsExtraRow}
            onClick={() => handleClickTab(SEARCH_TAB.DATES)}
            selectedTab={selectedTab}
            selectedDates={dateTabText}
            isStayFilterEnabled={isStayFilterEnabled}
          />
          <GuestsTab
            onClick={() => handleClickTab(SEARCH_TAB.DETAILS)}
            selectedTab={selectedTab}
            guests={guests}
            isStayFilterEnabled={isStayFilterEnabled}
            isGuestOccupancyAvailable={isGuestOccupancyAvailable}
          />
          <SearchButton
            selectedTab={selectedTab}
            loading={loading}
            isTabActive={selectedTab === SEARCH_TAB.DETAILS}
            isCategoryEnabled={isCategoryEnabled}
            isStayFilterEnabled={isStayFilterEnabled}
          />
        </div>
        {selectedTab && (
          <div
            data-tab={selectedTab}
            data-istowable={isTowable}
            className={`bg-white ${classes.searchDropdown}`}>
            {groupOptions.length > 0 && (
              <LocationSearchContent
                open={selectedTab === SEARCH_TAB.LOCATION}
                groupOptions={groupOptions}
                allGroupOptions={allGroupOptions}
                getListboxProps={getListboxProps}
                getOptionProps={getOptionProps}
                isDelivery={isDelivery}
              />
            )}
            <DateContent
              dates={dates}
              onChangeDates={handleChangeDate}
              open={selectedTab === SEARCH_TAB.DATES}
            />
            <DetailsContent
              guests={guests}
              onChangeGuests={onChangeGuests}
              onConfirmGuests={onConfirmGuests}
              onSubmit={onSubmit}
              open={selectedTab === SEARCH_TAB.DETAILS}
              hidePetFriendly={openedTab?.tab === EHeaderTab.PET_FRIENDLY}
              isGuestOccupancyAvailable={isGuestOccupancyAvailable}
              isCampground={isCampground}
            />
            {isTowable ? (
              <VehicleContent
                onChangeVehicle={handleChangeVehicle}
                open={selectedTab === SEARCH_TAB.VEHICLE}
                vehicle={vehicle}
              />
            ) : isRVSite ? (
              <VehicleCampgroundsContent
                onChangeVehicle={handleChangeVehicleCampgrounds}
                open={selectedTab === SEARCH_TAB.VEHICLE_CAMPGROUNDS}
                vehicle={vehicleCampgrounds}
              />
            ) : isLodgingSite ? (
              <LodgingCampgroundsContent
                onChangeLodging={handleChangeLodgingCampgrounds}
                open={selectedTab === SEARCH_TAB.LODGING_CAMPGROUNDS}
                lodging={lodgingCampgrounds}
              />
            ) : null}
          </div>
        )}
      </form>
    </>
  );
};

export default DesktopUniversalSearch;
